import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorAlert, Field } from '../components/ui';

export function LoginPage() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/login', { email, password });
      await refresh();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function forgot() {
    setError(null);
    if (!email) {
      setForgotMsg('Informe seu e-mail acima e clique novamente.');
      return;
    }
    try {
      const res = await api.post<{ message: string }>('/auth/password-reset/request', { email });
      setForgotMsg(res.message);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="auth-page login-page">
      <form className="auth-card form" onSubmit={submit} noValidate>
        <div>
          <div className="logo">TECHNOVE</div>
          <p className="muted">CRM · relacionamento e atendimento</p>
        </div>
        <ErrorAlert error={error} title="Não foi possível entrar." />
        {forgotMsg && <div className="alert alert-info">{forgotMsg}</div>}
        <Field label="E-mail" required>
          {(p) => <input {...p} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Senha" required>
          {(p) => <input {...p} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
        <button className="btn btn-primary" disabled={busy || !email || !password}>
          {busy ? 'Entrando…' : 'Entrar'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => void forgot()}>
          Esqueci minha senha
        </button>
        <AppLinks />
      </form>
    </div>
  );
}

// Atalhos para os outros aplicativos da TECHNOVE (o mesmo bloco existe no Gestão de Projetos e no Horizonte).
function AppLinks() {
  return (
    <nav className="app-links" aria-label="Outros aplicativos TECHNOVE">
      <span className="muted">Outros aplicativos TECHNOVE</span>
      <a href="https://projetos.technove.cloud">Gestão de Projetos</a>
      <a href="https://horizonte.technove.cloud/horizonte/">Horizonte (financeiro)</a>
    </nav>
  );
}

export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const { refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && next !== confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setDone(true);
      await refresh();
      if (!forced) navigate('/');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const form = (
    <form className="form" onSubmit={submit} noValidate>
      {forced && (
        <div className="alert alert-info">
          <strong>Primeiro acesso</strong>Defina uma senha pessoal para continuar. A senha temporária deixa de valer.
        </div>
      )}
      <ErrorAlert error={error} />
      {done && <div className="alert alert-success">Senha alterada.</div>}
      <Field label={forced ? 'Senha temporária' : 'Senha atual'} required>
        {(p) => <input {...p} type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />}
      </Field>
      <Field label="Nova senha" hint="Mínimo de 10 caracteres, com letras e números." required>
        {(p) => <input {...p} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
      </Field>
      <Field label="Confirme a nova senha" error={mismatch ? 'As senhas não coincidem.' : undefined} required>
        {(p) => <input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
      </Field>
      <div className="actions">
        <button className="btn btn-primary" disabled={busy || !current || !next || mismatch || !confirm}>
          Salvar nova senha
        </button>
        {forced && (
          <button type="button" className="btn" onClick={() => void logout()}>
            Sair
          </button>
        )}
      </div>
    </form>
  );

  if (forced) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="logo">TECHNOVE</div>
          <h1 style={{ fontSize: '1.2rem', marginTop: '0.5rem' }}>Defina sua senha</h1>
          {form}
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="page-head">
        <h1>Alterar minha senha</h1>
      </div>
      <div className="card" style={{ maxWidth: 520 }}>
        {form}
      </div>
    </>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const invite = params.get('convite') === '1';
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const mismatch = confirm.length > 0 && next !== confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/auth/password-reset/confirm', { token, newPassword: next });
      setDone(true);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="logo">TECHNOVE</div>
        <h1 style={{ fontSize: '1.2rem', marginTop: '0.5rem' }}>{invite ? 'Primeiro acesso: crie sua senha' : 'Redefinir senha'}</h1>
        {invite && !done && <p className="small muted">Depois de criar a senha, entre com o seu e-mail de login.</p>}
        {done ? (
          <div className="alert alert-success">
            {invite ? 'Senha criada.' : 'Senha redefinida.'} <Link to="/">Entrar</Link>
          </div>
        ) : (
          <form className="form" onSubmit={submit} noValidate>
            {!token && <div className="alert alert-error">Link incompleto. Peça um novo link a um administrador.</div>}
            <ErrorAlert error={error} />
            <Field label={invite ? 'Senha' : 'Nova senha'} hint="Mínimo de 10 caracteres, com letras e números." required>
              {(p) => <input {...p} type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />}
            </Field>
            <Field label={invite ? 'Confirme a senha' : 'Confirme a nova senha'} error={mismatch ? 'As senhas não coincidem.' : undefined} required>
              {(p) => <input {...p} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
            </Field>
            <button className="btn btn-primary" disabled={!token || !next || mismatch || !confirm}>
              {invite ? 'Criar senha' : 'Redefinir'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
